import React from "react";
import { notify } from "../Notification";
import { Chevron, Drive, Thing, formatCreated, formatSize, lookOf } from "./shared";
import notesIcon from "../../assets/icons/macos/notes.png";
import { ReactComponent as ExportIcon } from "../../assets/svgs/sf-symbols/orbstack/square.and.arrow.up.svg";

// The details pane, as OrbStack lays it out: grouped rows of what something is, then sections
// for its settings and what it's connected to. Connected things open in their own section.

const Group = ({ children }) => <div className="ob-group">{children}</div>;

const Field = ({ label, value, mono }) => (
  <div className="ob-field">
    <span className="ob-field-label">{label}</span>
    {value !== undefined && (
      <span className={`ob-field-value ${mono ? "is-mono" : ""}`} title={value}>
        {value}
      </span>
    )}
  </div>
);

const Section = ({ title, children }) => (
  <>
    <h3 className="ob-heading">{title}</h3>
    {children}
  </>
);

const LinkRow = ({ icon, label, detail, onClick }) => (
  <button type="button" className="ob-link-row" onClick={onClick}>
    {icon}
    <span className="ob-link-text">
      <span className="ob-link-label">{label}</span>
      {detail && <span className="ob-link-detail">{detail}</span>}
    </span>
    <Chevron />
  </button>
);

const containerLink = (images, container, onShow) => (
  <LinkRow
    key={container.id}
    icon={<Thing look={lookOf(images, container.image)} status={container.running ? "running" : "stopped"} />}
    label={container.name}
    detail={container.project || container.image}
    onClick={() => onShow(container)}
  />
);

export const ImageInfo = ({ image, images, usedBy, onShowContainer }) => {
  const config = [
    ["Command", image.command, true],
    ["Entrypoint", image.entrypoint, true],
    ["Working Directory", image.workdir],
    ["Stop Signal", image.stopSignal],
  ].filter(([, value]) => value);
  return (
    <div className="ob-info">
      <Group>
        <Field label="ID" value={image.id} mono />
        <Field label="Tag" value={image.ref} />
        <Field label="Created" value={formatCreated(image.created)} />
        <Field label="Size" value={formatSize(image.size)} />
        <Field label="Platform" value={image.platform} />
      </Group>
      <Group>
        <LinkRow
          icon={
            <span className="ob-link-icon ob-link-icon--export">
              <ExportIcon />
            </span>
          }
          label="Export"
          onClick={() => notify("Export saves the image as a file on my Mac, so it isn't in this replica.")}
        />
      </Group>
      {image.resume && (
        <Group>
          <LinkRow
            icon={<img src={notesIcon} alt="" className="ob-link-icon" draggable={false} />}
            label="Show in Resume"
            detail={image.resume.text}
            onClick={() =>
              window.dispatchEvent(new CustomEvent("open-resume", { detail: { anchor: image.resume.anchor } }))
            }
          />
        </Group>
      )}
      {config.length > 0 && (
        <Section title="Config">
          <Group>
            {config.map(([label, value, mono]) => (
              <Field key={label} label={label} value={value} mono={mono} />
            ))}
          </Group>
        </Section>
      )}
      {image.env.length > 0 && (
        <Section title="Environment">
          <div className="ob-table" role="table" aria-label="Environment">
            <div className="ob-table-row ob-table-head" role="row">
              <span role="columnheader">Key</span>
              <span role="columnheader">Value</span>
            </div>
            {image.env.map(([key, value]) => (
              <div key={key} className="ob-table-row" role="row">
                <span role="cell">{key}</span>
                <span role="cell" title={value}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
      {image.volumes.length > 0 && (
        <Section title="Volumes">
          <Group>
            {image.volumes.map((path) => (
              <Field key={path} label={path} />
            ))}
          </Group>
        </Section>
      )}
      {image.ports.length > 0 && (
        <Section title="Exposed Ports">
          <Group>
            {image.ports.map((port) => (
              <Field key={port} label={port} />
            ))}
          </Group>
        </Section>
      )}
      {usedBy.length > 0 && (
        <Section title="Used By">
          <Group>{usedBy.map((c) => containerLink(images, c, onShowContainer))}</Group>
        </Section>
      )}
    </div>
  );
};

export const ContainerInfo = ({ container, images, volumes, image, onShowImage, onShowVolume }) => (
  <div className="ob-info">
    <Group>
      <Field label="ID" value={container.id} mono />
      <Field label="Image" value={container.image} />
      <Field label="Status" value={container.running ? "Running" : "Stopped"} />
      <Field label="Created" value={formatCreated(container.created)} />
    </Group>
    {image && (
      <Group>
        <LinkRow
          icon={<Thing look={image.look} />}
          label={image.ref}
          detail={formatSize(image.size)}
          onClick={() => onShowImage(image)}
        />
      </Group>
    )}
    {container.ports.length > 0 && (
      <Section title="Ports">
        <Group>
          {container.ports.map((port) => {
            const [host, inside] = port.split(/:(.*)/);
            return <Field key={port} label={`localhost:${host}`} value={inside} />;
          })}
        </Group>
      </Section>
    )}
    {container.mounts.length > 0 && (
      <Section title="Volumes">
        <Group>
          {container.mounts.map(([name, path]) => (
            <LinkRow
              key={name}
              icon={<Drive look={(volumes.find((v) => v.name === name) || { look: "gray" }).look} />}
              label={name}
              detail={path}
              onClick={() => onShowVolume(name)}
            />
          ))}
        </Group>
      </Section>
    )}
  </div>
);

export const VolumeInfo = ({ volume, images, users, onShowContainer }) => (
  <div className="ob-info">
    <Group>
      <Field label="Name" value={volume.name} />
      <Field label="Size" value={formatSize(volume.size)} />
      <Field label="Driver" value="local" />
      <Field label="Mountpoint" value={`/var/lib/docker/volumes/${volume.name}/_data`} mono />
    </Group>
    {users.length > 0 && (
      <Section title="Used By">
        <Group>{users.map((c) => containerLink(images, c, onShowContainer))}</Group>
      </Section>
    )}
  </div>
);
